import React, { useEffect, useState } from "react";
import { profile as pApi, plans as plansApi, payments, auth } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { LogOut, Crown, Sparkles } from "lucide-react";

export default function SettingsTab({ user, refreshUser, onLogout }) {
  const [name, setName] = useState(user?.name || "");
  const [partyName, setPartyName] = useState(user?.party_name || "");
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState([]);
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    plansApi.list().then(setPlans);
  }, []);

  useEffect(() => {
    setName(user?.name || "");
    setPartyName(user?.party_name || "");
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      await pApi.update({ name, party_name: partyName });
      toast.success("Profile updated");
      refreshUser();
    } catch (e) {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  const startCheckout = async (planId) => {
    setBuying(planId);
    try {
      const order = await payments.createOrder(planId);
      const cf = window.Cashfree({ mode: order.mode });
      await cf.checkout({
        paymentSessionId: order.payment_session_id,
        redirectTarget: "_modal",
      });
      // After modal closes, refresh
      setTimeout(() => refreshUser(), 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment failed");
    } finally {
      setBuying(null);
    }
  };

  const currentPlan = plans.find((p) => p.id === user?.plan);

  return (
    <div className="space-y-6">
      {/* Profile */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-950/70 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4 text-fuchsia-400" />
          <span className="font-mono text-[10px] tracking-widest text-zinc-500">PROFILE</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-zinc-400 mb-1 block">Your Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-zinc-900 border-zinc-800 text-white"
              data-testid="settings-name-input"
            />
          </div>
          <div>
            <Label className="text-xs text-zinc-400 mb-1 block">Party Name</Label>
            <Input
              value={partyName}
              onChange={(e) => setPartyName(e.target.value)}
              className="bg-zinc-900 border-zinc-800 text-white"
              data-testid="settings-party-input"
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button
            onClick={save}
            disabled={saving}
            className="bg-fuchsia-600 hover:bg-fuchsia-500"
            data-testid="settings-save-btn"
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      {/* Subscription */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-950/70 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Crown className="w-4 h-4 text-yellow-500" />
          <span className="font-mono text-[10px] tracking-widest text-zinc-500">SUBSCRIPTION</span>
        </div>
        <div className="mb-4 flex items-center justify-between p-3 rounded-lg bg-zinc-900/60 border border-zinc-800">
          <div>
            <div className="text-sm text-zinc-400">Current Plan</div>
            <div className="font-heading font-bold text-lg">
              {currentPlan ? currentPlan.name : "None"}
            </div>
            {user?.plan_expires_at && (
              <div className="text-xs text-zinc-500 mt-1" data-testid="settings-expiry">
                {formatExpiry(user)}
              </div>
            )}
          </div>
          <div className="text-right">
            <div className={`px-2 py-1 rounded-md text-[10px] font-mono tracking-widest ${user?.plan_active ? "bg-green-500/10 text-green-400" : "bg-zinc-800 text-zinc-500"}`}>
              {user?.plan_active ? "ACTIVE" : "INACTIVE"}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {plans.map((p) => (
            <div
              key={p.id}
              className="rounded-lg border border-zinc-800 p-4 bg-zinc-950 hover:bg-zinc-900/50 transition-all"
            >
              <div className="flex items-baseline justify-between">
                <div className="font-heading font-bold">{p.name}</div>
                <div className="font-mono font-black text-xl">₹{p.amount}</div>
              </div>
              <div className="text-xs text-zinc-500 mt-1 mb-3">{p.description}</div>
              <Button
                onClick={() => startCheckout(p.id)}
                disabled={buying === p.id}
                className="w-full bg-white text-black hover:bg-zinc-200"
                data-testid={`buy-${p.id}`}
              >
                {buying === p.id
                  ? "Processing..."
                  : user?.plan === p.id
                  ? "Renew / Extend"
                  : user?.plan
                  ? "Switch Plan"
                  : "Buy Now"}
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Logout */}
      <div className="flex justify-end">
        <Button
          variant="outline"
          onClick={onLogout}
          className="border-zinc-800 text-zinc-400 hover:text-red-400 hover:border-red-500/50"
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" /> Log out
        </Button>
      </div>
    </div>
  );
}

function formatExpiry(user) {
  if (!user?.plan_expires_at) return "";
  const isSingle = user.plan === "single_party_99";
  const exp = new Date(user.plan_expires_at);
  const now = new Date();
  const diffMs = exp - now;
  if (diffMs <= 0) return `Expired on ${exp.toLocaleString()}`;

  if (isSingle) {
    const hours = Math.floor(diffMs / 3600000);
    const mins = Math.floor((diffMs % 3600000) / 60000);
    return `${hours}h ${mins}m remaining · expires ${exp.toLocaleString(undefined, {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  }
  const days = Math.floor(diffMs / 86400000);
  return `${days} day${days === 1 ? "" : "s"} remaining · expires ${exp.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  })}`;
}
